/**
 * @module
 * @exports ready
 * @description The core dispatcher for the dom ready event javascript.
 */

import { on, ready } from '@utils/events';
import activateJS from '@utils/dom/activate';

import modules from '../modules/index';
import single from '../single/index';
import content from '../content/index';
import resize from './resize';
import plugins from './plugins';
import viewportDims from './viewport-dims';

// Custom debounce function
const debounce = (func, wait, immediate = false) => {
	let timeout;
	return function executedFunction(...args) {
		const context = this;
		const later = () => {
			timeout = null;
			if (!immediate) func.apply(context, args);
		};
		const callNow = immediate && !timeout;
		clearTimeout(timeout);
		timeout = setTimeout(later, wait);
		if (callNow) func.apply(context, args);
	};
};

/**
 * @function bindEvents
 * @description Bind global event listeners here,
 */

const bindEvents = () => {
	on(window, 'resize', debounce(resize, 200, false));
};

/**
 * @function browserSupportsAllFeatures
 * @description Add feature detects here like window.IntersectionObserver || window.Fetch etc
 */

const browserSupportsAllFeatures = () => window.IntersectionObserver;

/**
 * @function init
 * @description The core dispatcher for init across the codebase.
 */

const init = () => {
	// init external plugins
	plugins();

	// set initial states
	viewportDims();

	// initialize global events
	bindEvents();

	// initialize the module scripts
	modules();

	// initialize the main scripts
	single();

	// initialize the content scripts
	content();

	activateJS();
};

/**
 * @function setupEnivironment
 * @description
 */

const setupEnvironment = () => {
	if (browserSupportsAllFeatures()) {
		init();
		return;
	}
};

/**
 * @function domReady
 * @description Export our dom ready enabled init.
 */

const domReady = () => {
	ready(setupEnvironment);
};

export default domReady;
